import {
  createEditEmployee,
  deleteEmployee,
  Employee,
  employeeKeys,
  getEmployeeById,
  getEmployees,
  subscribeToEmployees,
  swapEmployeePositions,
} from '@/api/apiEmployees';
import { EmployeeFormValues } from '@/components/employees_tsx/employee-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'react-hot-toast';

export function useEmployees() {
  const queryClient = useQueryClient();

  // Query to fetch all employees
  const employeesQuery = useQuery({
    queryKey: employeeKeys.lists(),
    queryFn: getEmployees,
  });

  // Query to fetch a single employee
  const useEmployee = (id: number) =>
    useQuery({
      queryKey: employeeKeys.detail(id),
      queryFn: () => getEmployeeById(id),
      enabled: !!id,
    });

  // Mutation to create/edit employee
  const createEditMutation = useMutation({
    mutationFn: ({ employee, id }: { employee: Partial<EmployeeFormValues>; id?: number }) =>
      createEditEmployee(employee, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      //toast.success('Employee saved successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Mutation to delete employee
  const deleteMutation = useMutation({
    mutationFn: deleteEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      toast.success('Employee deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Mutation to swap employee positions
  const swapPositionMutation = useMutation({
    mutationFn: ({
      currentRow,
      targetRow,
      direction,
    }: {
      currentRow: Employee;
      targetRow: Employee;
      direction: 'up' | 'down';
    }) => swapEmployeePositions(currentRow, targetRow, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Set up realtime subscription
  useEffect(() => {
    const unsubscribe = subscribeToEmployees(
      // Handle updates
      ({ new: newEmployee, old: oldEmployee }) => {
        queryClient.setQueryData<Employee[]>(employeeKeys.lists(), oldData => {
          if (!oldData) return oldData;
          return oldData.map(employee => (employee.id === oldEmployee.id ? newEmployee : employee));
        });
      },
      // Handle deletions
      ({ old: oldEmployee }) => {
        queryClient.setQueryData<Employee[]>(employeeKeys.lists(), oldData => {
          if (!oldData) return oldData;
          return oldData.filter(employee => employee.id !== oldEmployee.id);
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  return {
    // Queries
    employees: employeesQuery.data ?? [],
    isLoading: employeesQuery.isLoading,
    error: employeesQuery.error,
    useEmployee,

    // Mutations
    createEditEmployee: createEditMutation.mutate,
    deleteEmployee: deleteMutation.mutate,
    swapEmployeePosition: swapPositionMutation.mutate,

    // Mutation states
    isCreatingOrEditing: createEditMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSwapping: swapPositionMutation.isPending,
  };
}
